import { FormatHelpers } from "../../helpers/format-helpers";
import { StringHelpers } from "../../helpers/string-helpers";
import { ValidationHelpers } from "../../helpers/validation-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleColorModel } from "../../models/chart-style/chart-style-color.model";
import { TradeOrderDataModel } from "../../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "../component.interface";
import { InformationPopupComponent } from "../information-popup.component";
import { MainSymbolCurrentPriceComponent } from "../main-symbol-current-price.component";
import { BracketTradePlotLineComponent } from "./bracket-trade-plot-line.component";
import { OrderBracketManagerComponent } from "../order-bracket-manager.component";
import { TradeOrderPriceHelper } from "../../helpers/trade-order-price-helper";

export class BracketOrderProfitTargetPlotLine implements IComponent {
    public id = 'ltp-' + StringHelpers.newGuid();

    public elementWidth: number | null = null;
    public elementHeight: number | null = null;
    public moveMode = false;
    public hide = false;
    public disabled = false;

    public tradeId = '';

    private base: IChartContainer;
    public model = new TradeOrderDataModel();
    public price = 0;
    public profit = 0;
    private profitString = '';
    private left = 0;
    private originalLeft = 0;
    private g: any = null;
    private bracketLine: any = null;
    private bracketIcon: any = null;
    private fObj: any = null;
    private bracketTradePlotLine: BracketTradePlotLineComponent | null = null;
    private divElement: HTMLElement | null = null;
    private lastPriceDivElement: HTMLElement | null = null;
    private removed = false;
    private color = new ChartStyleColorModel();
    private popup: InformationPopupComponent | null;
    private orderBracketManager: OrderBracketManagerComponent | null;

    private mouseUpHandler: EventListener;

    constructor(base: IChartContainer, bracketTradePlotLine: BracketTradePlotLineComponent, left: number, model: TradeOrderDataModel) {
        this.base = base;
        this.model = model;
        this.price = model.price;
        this.bracketTradePlotLine = bracketTradePlotLine;
        this.originalLeft = left;
        this.tradeId = model.id;
        this.color = this.bracketTradePlotLine.getColor(true);
        this.popup = this.base.getComponentsByType(InformationPopupComponent)?.[0] as InformationPopupComponent;
        this.orderBracketManager = this.base.getComponentsByType(OrderBracketManagerComponent)?.[0] as OrderBracketManagerComponent;

        this.calculateElementSize();
        this.mouseUpHandler = this.mouseUp.bind(this);
        this.createSytle();
        this.init();
        this.onEvents();

        if (this.orderBracketManager.selectedGroupId === this.bracketTradePlotLine.model?.id) {
            this.enable();
            this.setBracketEnabled(true);
        } else {
            this.disable();
            this.setBracketEnabled(false);
        }
    }

    public onExtreamChange() {
        if (this.removed || this.moveMode) return;
        this.createIcon();
        this.createLastPrice();
    }

    public onRedraw() {
        if (this.removed || this.moveMode) return;
        this.createIcon();
        this.createLastPrice();
    }

    public enable() {
        if (this.bracketTradePlotLine?.isPending) return;
        this.disabled = false;
        this.divElement?.setAttribute('style', this.hide ? 'display: none;' : '');
        this.g?.attr({ zIndex: 20 });
    }

    public disable() {
        this.disabled = true;
        this.divElement?.setAttribute('style', this.hide ? 'display: none;' : 'opacity: 0.3 !important; cursor: default !important;');
        this.g?.attr({ zIndex: 15 });
    }

    public setBracketEnabled(enabled: boolean) {
        const opacity = enabled ? 1 : 0.3;
        this.bracketLine?.attr({ 'opacity': opacity });
        this.bracketIcon?.attr({ 'opacity': opacity });
    }

    public remove() {
        this.removed = true;
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        yAxis?.removePlotLine(this.id);
        this.removeStyle();

        this.lastPriceDivElement?.remove();

        try {
            this.g?.destroy();
        } catch { }

        try {
            this.bracketLine?.destroy();
        } catch { }

        try {
            this.bracketIcon?.destroy();
        } catch { }

        this.fObj = null;

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);
        this.base.tiChartSeries.element.removeEventListener('mouseup', this.mouseUpHandler);
    }

    private calculateElementSize() {
        this.setElementWidthAndHeight();
        this.left = (this.originalLeft + this.bracketTradePlotLine.elementWidth) - this.elementWidth;
    }

    private setElementWidthAndHeight() {
        const element = this.getTargetPriceElement();
        element.setAttribute('style', 'opacity: 0 !important; visibility: hidden !important; position: absolute !important; width: auto !important');
        this.base.tiChartSeries?.element?.insertAdjacentElement('beforeend', element);
        const width = element.clientWidth;
        const height = element.clientHeight;
        this.elementWidth = Math.ceil(width);
        this.elementHeight = Math.ceil(height);
        element.remove();
    }

    private createLastPrice() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const currentYValue = yAxis.toPixels(this.price) ?? 0;
        const myHeight = 7;

        const valueTop = (yAxis.toPixels(yAxis.max) ?? 0) - 16;
        const valueBottom = (yAxis.toPixels(yAxis.min) ?? 0) - 16;

        let top = currentYValue - myHeight;
        let hide = false;

        if (this.price > yAxis.max || valueTop > top) {
            top = valueTop;
            hide = true;
        } else if (this.price < yAxis.min || top > valueBottom) {
            top = valueBottom;
            hide = true;
        }

        const priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.price, true);

        if (!this.lastPriceDivElement) {
            this.lastPriceDivElement = this.base.chartWrapper.querySelector<HTMLElement>('.last-price-wrapper.limit-profit-target.' + this.id);
        }

        if (this.lastPriceDivElement) {
            this.lastPriceDivElement.style.top = top + 'px';

            const pTag = this.lastPriceDivElement.querySelector('p');
            if (pTag) {
                pTag.innerHTML = priceToString;
            }
        } else {
            this.lastPriceDivElement = document.createElement('div');
            this.lastPriceDivElement.className = `last-price-wrapper limit-profit-target ${this.id}`;
            this.lastPriceDivElement.style.top = top + 'px';
            this.lastPriceDivElement.innerHTML = `
                <div class="last-price">
                    <p>${priceToString}</p>
                    <div class="line"></div>
                </div>
            `;

            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.lastPriceDivElement);
            }
        }

        if (hide) {
            this.lastPriceDivElement?.classList.add('hidden');
        } else {
            this.lastPriceDivElement?.classList.remove('hidden');
        }
    }

    private getTargetPriceElement(dragging?: boolean): HTMLElement {
        const avgCost = (this.bracketTradePlotLine?.position?.averageCost ?? 1);
        const sharesCount = this.model.shares ?? 0;

        this.profit = sharesCount * (this.price - avgCost);

        const profitVal = Math.abs(this.profit);
        this.profitString = FormatHelpers.numberToStringThousandSeparators(profitVal, true, 0);
        const priceString = FormatHelpers.numberToStringThousandSeparators(this.price, true);

        const lineWidth = this.bracketTradePlotLine.getOrderLineWidth(this.model?.entryTime);
        const color = this.color.toRGB();

        const tooltip = `Limit Order Profit Target: @$${priceString}, select "X" to Cancel`;

        const divElement = document.createElement('div');
        divElement.title = tooltip;
        divElement.className = `limit-profit-target-wrapper`;
        divElement.setAttribute('style', dragging ? `cursor: -webkit-grabbing` : ``);
        divElement.innerHTML = `
            <span class="line" style="position: absolute; height: 1px; top: 10px;width: ${lineWidth}px; background-image: repeating-linear-gradient(to right, ${color} 0px, ${color} 3px, transparent 3px, transparent 6px);"></span>
            <div class="left item" style="background-color: #fff; color: ${color};">
                <p class="value">${Math.abs(sharesCount)}</p>
            </div>
            <div class="center item">
                <p class="value ${this.profit >= 0 || this.profitString == '0' ? '' : 'neg'}">
                    ${this.profit == 0 || this.profitString == '0' ? '' : (this.profit > 0 ? '+' : '-')}$${this.profitString}
                </p>
            </div>
            <div class="right item">
                <svg class="exit-order" >
                    <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                </svg>
            </div>
        `;

        return divElement;
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke: transparent !important;}`;

        styleTag += `
            .last-price-wrapper.limit-profit-target.${className} {
                background-color: ${this.color.toRGB()} !important;
            }
            .last-price-wrapper.limit-profit-target.${className} .line {
                position: absolute;
                right: 0px;
                top: 50%;
                background-color: ${this.color.toRGB()} !important;
                height: 1px;
            }
            .last-price-wrapper.limit-profit-target.${className} .last-price p {
                z-index: 1;
                position: relative;
            }
            .last-price-wrapper.limit-profit-target.${className} .last-price:before {
                border-color: transparent ${this.color.toRGB()} transparent transparent !important;
            }
        `;

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.price,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 8
            });

            this.createIcon();
            this.createLastPrice();
        }
    }

    private mouseUpTimer: any;
    private mouseUp() {
        if (!this.moveMode || this.disabled) {
            return;
        }

        this.moveMode = false;

        if (this.divElement) {
            this.divElement.style.cursor = '';
        }

        this.base.chartService.requestUpdateChartTradeV3(this.tradeId, this.price);
        (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.showAddButton();
        clearTimeout(this.mouseUpTimer);
        this.mouseUpTimer = setTimeout(() => {
            this.base.tiChartSeries.preventDragging = false;

            if (!!this.g?.element) {
                this.g?.attr({ zIndex: this.disabled ? 15 : 20 });
            }
        }, 500);
    }

    private reAddPlotline(yValue: number) {
        const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
        if (!yAxis) {
            return;
        }

        TradeOrderPriceHelper.getRoundedPrice(yValue);
        if (!ValidationHelpers.isValidNumber(yValue)) {
            return;
        }

        this.price = yValue;
        yAxis.removePlotLine(this.id);
        yAxis.addPlotLine({
            value: yValue,
            className: this.id,
            width: 1,
            id: this.id,
            zIndex: 8
        });

        this.calculateElementSize();
        this.createIcon(true);
        this.createLastPrice();
    }

    private onEvents() {
        this.base.domEvents.onMouseMove(this.id, (event: any) => {
            if (!this.moveMode || !this.base.tiChartSeries || this.disabled) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            if (this.divElement) {
                this.divElement.style.cursor = '-webkit-grabbing';
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);

            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            this.hide = false;

            const yValue = yAxis.toValue(yTop);
            let yValueValidTop = yAxis.toValue(yTop + 10);
            let yValueValidBottom = yAxis.toValue(yTop - 10);
            if (yValueValidTop < yAxis.min || yValueValidBottom > yAxis.max) {
                return;
            }

            (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.hideAddButton();
            this.reAddPlotline(yValue);
        });

        this.base.tiChartSeries.element.addEventListener('mouseup', this.mouseUpHandler);
    }

    private createIcon(dragging?: boolean) {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        try {
            if (this.g) {
                this.g?.destroy();
            }
        } catch { }

        if (dragging) {
            this.base.tiChartSeries.preventDragging = true;
            this.moveMode = true;

            if (this.divElement)
                this.divElement.style.cursor = '-webkit-grabbing';
        }

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        this.g = renderer.g(this.id).attr({
            zIndex: this.disabled ? 15 : 20
        });

        let top = yAxis.toPixels(this.price, true);
        if (this.base.element.containClass('one')) {
            top += this.base.marketingWebChartMode ? 26 : 14;
        } else {
            top += this.base.marketingWebChartMode ? 46 : 33;
        }

        this.hide = false;
        if (this.price > yAxis.max || this.price < yAxis.min) {
            this.hide = true;
        }

        this.divElement = this.getTargetPriceElement(dragging);

        if (this.disabled) {
            this.disable();
        }

        if (this.divElement) {
            if (this.hide) {
                this.divElement.style.display = 'none';
            } else {
                this.divElement.style.display = '';
            }
        }

        const foreignObjectElement = renderer.createElement('foreignObject');
        foreignObjectElement.element.appendChild(this.divElement);
        foreignObjectElement.element.setAttribute('width', this.elementWidth);
        foreignObjectElement.element.setAttribute('height', 25 + '');
        foreignObjectElement.element.setAttribute('overflow', 'visible');
        foreignObjectElement.element.setAttribute('x', this.left);
        foreignObjectElement.element.setAttribute('y', top);
        foreignObjectElement.add(this.g);

        this.fObj = foreignObjectElement;
        this.g.add();
        this.addBracketLineIfNotExist();

        if (!this.divElement || this.divElement.containClass('inited')) {
            return;
        }

        let hoverOnRemove = false;
        let hoverOnRemoveTimer: any = null;
        this.divElement.addClass('inited');
        this.divElement?.addEventListener('mousedown', () => {
            if (this.disabled) return;
            this.base.tiChartSeries.preventDragging = true;

            if (hoverOnRemove) return;
            this.moveMode = true;
        });

        this.divElement?.addEventListener('click', () => {
            if (this.bracketTradePlotLine?.model?.id) {
                this.orderBracketManager?.selectGroup(this.bracketTradePlotLine.model.id);
            }
        });

        this.divElement.findFirst('.right')?.ifExistThen((r: HTMLElement) => {
            const remove = () => {
                if (!this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel) return;
                if (this.disabled) return;

                if (this.base.tiChartSeries?.chartStyleSettings.chartSettingsPopupModel.displayOrderConfirmationPopup) {
                    this.popup?.show(`Profit Target`, this.getCloseTargetProfitMessage(), () => {
                        this.bracketTradePlotLine?.closeProfitTarget();
                    });
                } else {
                    this.bracketTradePlotLine?.closeProfitTarget();
                }
            };

            if (r.classList.contains('inited')) {
                return;
            }
            r.classList.add('inited');
            r.addEventListener('mouseenter', () => {
                clearTimeout(hoverOnRemoveTimer);
                r.classList.add('hover');
                hoverOnRemove = true;
            });
            r.addEventListener('mouseleave', () => {
                r.classList.remove('hover');
                hoverOnRemoveTimer = setTimeout(() => {
                    hoverOnRemove = false;
                }, 300);
            });
            r.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                remove();
            });

            const s = r.querySelector('svg')
            if (!s || s.classList.contains('inited')) {
                return;
            }
            s.classList.add('inited');
            s.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                remove();
            });
        });
    }

    private addBracketLineIfNotExist() {

        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        const color = this.color;
        if (this.bracketLine) {
            try {
                this.bracketLine?.destroy();
                this.bracketLine = null;
            } catch { }
        }

        if (this.bracketIcon) {
            try {
                this.bracketIcon?.destroy();
                this.bracketIcon = null;
            } catch { }
        }

        this.bracketTradePlotLine.setLineRight(`.limit-profit-target-wrapper`, false, this.model?.entryTime);

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        const width = this.elementWidth;

        const EXTRA_TOP = this.base.element.containClass('one')
            ? (this.base.marketingWebChartMode ? 38 : 26)
            : (this.base.marketingWebChartMode ? 58 : 45);
        const currentY = yAxis.toPixels(this.price, true) + EXTRA_TOP;
        let y1 = currentY;
        let y2 = yAxis.toPixels(this.bracketTradePlotLine?.position?.averageCost, true) + EXTRA_TOP + 2;
        const left = this.left + width + 9;

        if (this.hide) {
            y1 = yAxis.toPixels(this.bracketTradePlotLine.isLong ? yAxis.max : yAxis.min, true) + EXTRA_TOP;

            if (this.bracketTradePlotLine.hide) {
                y2 = yAxis.toPixels(this.bracketTradePlotLine.isLong ? yAxis.min : yAxis.max, true) + EXTRA_TOP;
            }

            this.bracketLine = renderer
                .path([`M ${left} ${y1} L ${left} ${y2} Z`])
                .attr({ 'stroke': `${color.toRGB()}`, 'stroke-width': 1, 'stroke-dasharray': '3 3' })
                .add();

            this.bracketTradePlotLine?.addBracketIcon();
            this.setBracketEnabled(!this.disabled);
            return;
        }

        if (!this.bracketTradePlotLine.hide) {
            this.bracketLine = renderer
                .path([`M ${left} ${y1} L ${left} ${y2} Z`])
                .attr({ 'stroke': `${color.toRGB()}`, 'stroke-width': 1, 'stroke-dasharray': '3 3' })
                .add();
        }

        this.bracketIcon = renderer.circle(left, Math.floor(currentY) + 1, 3)
            .attr({
                'fill': 'white',
                'stroke': `${color.toRGB()}`,
                'stroke-width': 1.5,
                'zIndex': 12
            })
            .add();

        this.bracketTradePlotLine.setLineRight(`.limit-profit-target-wrapper`, true, this.model?.entryTime);
        this.bracketTradePlotLine?.addBracketIcon();
        this.setBracketEnabled(!this.disabled);
    }

    public getCloseTargetProfitMessage(): string {
        const priceString = FormatHelpers.numberToStringThousandSeparators(Math.abs(this.price), true, 2);
        const html = `Remove Profit Target limit order at $${priceString}?`;

        return `<span class="trade-message">${html}</span>`;
    }
}
